<template>
  <div class="ed-select-tipo" :class="{ 'is-disabled': disabled, 'is-focus': open }" ref="trigger" @click="toggle">
    <!-- TRIGGER -->
    <div class="ed-select-tipo__trigger">
      <!-- TAGS (multiple) -->
      <template v-if="multiple && selectedItems.length">
        <span class="ed-select-tipo__tag" v-for="(tag, i) in visibleTags" :key="i">
          {{ tag.descricao }}
          <i class="fa fa-times ed-select-tipo__tag-close" @click.stop="removeTag(tag)" v-if="!disabled"></i>
        </span>
        <span class="ed-select-tipo__tag ed-select-tipo__tag--count" v-if="hiddenCount > 0">
          +{{ hiddenCount }}
        </span>
      </template>

      <!-- VALOR SELECIONADO (single) -->
      <span class="ed-select-tipo__value" v-else-if="!multiple && selectedLabel">
        {{ selectedLabel }}
      </span>

      <!-- PLACEHOLDER -->
      <span class="ed-select-tipo__placeholder" v-else>
        {{ placeholder }}
      </span>

      <!-- ICONS -->
      <i class="fa fa-times ed-select-tipo__clear" v-if="clearable && hasValue && !disabled" @click.stop="clear"></i>
      <i class="fa fa-caret-down ed-select-tipo__arrow" :class="{ 'is-reverse': open }"></i>
    </div>

    <!-- DROPDOWN (teleported to body) -->
    <teleport-dropdown
      :visible="open"
      :trigger-el="$refs.trigger"
      @close="close"
    >
      <!-- BUSCA -->
      <div class="ed-select-tipo__search" v-if="filteredItems.length > 7 || search">
        <input
          ref="searchInput"
          v-model="search"
          class="ed-select-tipo__search-input"
          placeholder="Buscar..."
          @click.stop
          @keydown.esc="close"
        />
      </div>

      <!-- OPÇÕES -->
      <div class="ed-select-tipo__options" ref="optionsList">
        <div
          v-for="item in filteredItems"
          :key="item.id"
          class="ed-select-tipo__option"
          :class="{ 'is-selected': isSelected(item), 'is-hover': hoverIndex === item.id }"
          @click.stop="select(item)"
          @mouseenter="hoverIndex = item.id"
        >
          <span class="ed-select-tipo__option-check" v-if="multiple">
            <i :class="isSelected(item) ? 'fa fa-check-square' : 'fa fa-square-o'"></i>
          </span>
          <div class="ed-select-tipo__option-content">
            <div>{{ item.descricao }}</div>
            <div class="ed-select-tipo__option-obs" v-if="item.obs">{{ item.obs }}</div>
          </div>
        </div>

        <div class="ed-select-tipo__empty" v-if="filteredItems.length === 0">
          Nenhum resultado
        </div>
      </div>
    </teleport-dropdown>
  </div>
</template>

<script>
var TeleportDropdown = {
  name: 'TeleportDropdown',
  props: {
    visible: Boolean,
    triggerEl: { default: null },
  },
  data: function () {
    return {
      dropdownEl: null,
      position: { top: 0, left: 0, width: 0 },
      openDirection: 'down',
    };
  },
  watch: {
    visible: function (val) {
      if (val) {
        this.mount();
      } else {
        this.unmount();
      }
    },
  },
  methods: {
    mount: function () {
      var self = this;

      if (!this.dropdownEl) {
        this.dropdownEl = document.createElement('div');
        this.dropdownEl.className = 'ed-select-tipo-dropdown';
        document.body.appendChild(this.dropdownEl);
      }

      this.updatePosition();

      this.$nextTick(function () {
        if (self.$refs.content) {
          self.dropdownEl.innerHTML = '';
          self.dropdownEl.appendChild(self.$refs.content);
        }
        self.dropdownEl.style.display = 'block';

        self._onScroll = function () {
          self.updatePosition();
        };
        self._onClickOutside = function (e) {
          if (
            self.dropdownEl &&
            !self.dropdownEl.contains(e.target) &&
            self.triggerEl &&
            !self.triggerEl.contains(e.target)
          ) {
            self.$emit('close');
          }
        };

        window.addEventListener('scroll', self._onScroll, true);
        window.addEventListener('resize', self._onScroll);
        document.addEventListener('mousedown', self._onClickOutside);
      });
    },

    unmount: function () {
      if (this.dropdownEl) {
        this.dropdownEl.style.display = 'none';
      }
      window.removeEventListener('scroll', this._onScroll, true);
      window.removeEventListener('resize', this._onScroll);
      document.removeEventListener('mousedown', this._onClickOutside);
    },

    updatePosition: function () {
      if (!this.triggerEl || !this.dropdownEl) return;

      var rect = this.triggerEl.getBoundingClientRect();
      var dropdownHeight = this.dropdownEl.offsetHeight || 250;
      var viewportHeight = window.innerHeight;
      var spaceBelow = viewportHeight - rect.bottom;
      var spaceAbove = rect.top;
      var top;

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        top = rect.top - dropdownHeight;
        this.openDirection = 'up';
      } else {
        top = rect.bottom;
        this.openDirection = 'down';
      }

      this.dropdownEl.style.position = 'fixed';
      this.dropdownEl.style.top = top + 'px';
      this.dropdownEl.style.left = rect.left + 'px';
      this.dropdownEl.style.width = rect.width + 'px';
      this.dropdownEl.style.zIndex = '99999';
    },
  },

  beforeDestroy: function () {
    this.unmount();
    if (this.dropdownEl && this.dropdownEl.parentNode) {
      this.dropdownEl.parentNode.removeChild(this.dropdownEl);
    }
  },

  render: function (h) {
    return h('div', { ref: 'content', style: { display: this.visible ? 'block' : 'none' } }, this.$slots.default);
  },
};

export default {
  name: 'EdSelectTipo',
  components: { TeleportDropdown: TeleportDropdown },
  props: {
    value: {
      type: [Number, String, Array],
      default: null,
    },
    items: {
      type: Array,
      default: function () {
        return [];
      },
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Selecione',
    },
    valorField: {
      type: String,
      default: null,
    },
  },

  data: function () {
    return {
      open: false,
      search: '',
      hoverIndex: null,
    };
  },

  computed: {
    safeItems: function () {
      return this.items || [];
    },

    filteredItems: function () {
      if (!this.search) return this.safeItems;
      var s = this.search.toLowerCase();
      return this.safeItems.filter(function (item) {
        return (
          (item.descricao && item.descricao.toLowerCase().indexOf(s) !== -1) ||
          (item.obs && item.obs.toLowerCase().indexOf(s) !== -1)
        );
      });
    },

    hasValue: function () {
      if (this.multiple) {
        return Array.isArray(this.value) && this.value.length > 0;
      }
      return this.value !== null && this.value !== '' && this.value !== undefined;
    },

    selectedItems: function () {
      var self = this;
      if (!this.multiple || !Array.isArray(this.value)) return [];
      return this.safeItems.filter(function (item) {
        var val = self.getItemValue(item);
        return self.value.indexOf(val) !== -1;
      });
    },

    visibleTags: function () {
      return this.selectedItems.slice(0, 1);
    },

    hiddenCount: function () {
      return Math.max(0, this.selectedItems.length - 1);
    },

    selectedLabel: function () {
      if (this.multiple || !this.hasValue) return '';
      var self = this;
      var found = this.safeItems.find(function (item) {
        return self.getItemValue(item) === self.value;
      });
      return found ? found.descricao : '';
    },
  },

  methods: {
    getItemValue: function (item) {
      if (this.valorField) {
        return item[this.valorField] !== undefined ? String(item[this.valorField]) : '';
      }
      return parseInt(item.id);
    },

    isSelected: function (item) {
      var val = this.getItemValue(item);
      if (this.multiple && Array.isArray(this.value)) {
        return this.value.indexOf(val) !== -1;
      }
      return this.value === val;
    },

    toggle: function () {
      if (this.disabled) return;
      if (this.open) {
        this.close();
      } else {
        this.openDropdown();
      }
    },

    openDropdown: function () {
      this.search = '';
      this.open = true;
      var self = this;
      this.$nextTick(function () {
        if (self.$refs.searchInput) {
          self.$refs.searchInput.focus();
        }
      });
    },

    close: function () {
      this.open = false;
      this.search = '';
    },

    select: function (item) {
      var val = this.getItemValue(item);

      if (this.multiple) {
        var arr = Array.isArray(this.value) ? this.value.slice() : [];
        var idx = arr.indexOf(val);
        if (idx !== -1) {
          arr.splice(idx, 1);
        } else {
          arr.push(val);
        }
        this.$emit('input', arr);
      } else {
        this.$emit('input', val);
        this.close();
      }
    },

    removeTag: function (item) {
      if (this.disabled) return;
      var val = this.getItemValue(item);
      var arr = Array.isArray(this.value) ? this.value.slice() : [];
      var idx = arr.indexOf(val);
      if (idx !== -1) {
        arr.splice(idx, 1);
      }
      this.$emit('input', arr);
    },

    clear: function () {
      this.$emit('input', this.multiple ? [] : null);
      this.close();
    },
  },

  beforeDestroy: function () {
    this.close();
  },
};
</script>

<style lang="scss">
.ed-select-tipo {
  position: relative;
  width: 100%;
  cursor: pointer;
  font-size: 14px;

  &.is-disabled {
    cursor: not-allowed;
    .ed-select-tipo__trigger {
      background-color: #f5f7fa;
      border-color: #e4e7ed;
      color: #c0c4cc;
    }
  }

  &.is-focus .ed-select-tipo__trigger {
    border-color: #409eff;
  }
}

.ed-select-tipo__trigger {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 36px;
  padding: 0 30px 0 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.2s;
  position: relative;

  &:hover {
    border-color: #c0c4cc;
  }
}

.ed-select-tipo__value {
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  line-height: 34px;
}

.ed-select-tipo__placeholder {
  color: #c0c4cc;
  flex: 1;
  line-height: 34px;
}

.ed-select-tipo__tag {
  display: inline-flex;
  align-items: center;
  background: #f0f2f5;
  border: 1px solid #e4e7ed;
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 12px;
  color: #909399;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 2px 0;
}

.ed-select-tipo__tag--count {
  background: #ecf5ff;
  color: #409eff;
  border-color: #d9ecff;
}

.ed-select-tipo__tag-close {
  margin-left: 4px;
  font-size: 10px;
  cursor: pointer;

  &:hover {
    color: #606266;
  }
}

.ed-select-tipo__arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #c0c4cc;
  font-size: 14px;
  transition: transform 0.3s;

  &.is-reverse {
    transform: translateY(-50%) rotate(180deg);
  }
}

.ed-select-tipo__clear {
  position: absolute;
  right: 26px;
  top: 50%;
  transform: translateY(-50%);
  color: #c0c4cc;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: #909399;
  }
}

/* DROPDOWN (renderizado no body) */
.ed-select-tipo-dropdown {
  display: none;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow: hidden;
}

.ed-select-tipo__search {
  padding: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.ed-select-tipo__search-input {
  width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 5px 8px;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #409eff;
  }
}

.ed-select-tipo__options {
  max-height: 240px;
  overflow-y: auto;
}

.ed-select-tipo__option {
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  color: #606266;
  transition: background-color 0.1s;

  &:hover,
  &.is-hover {
    background-color: #f5f7fa;
  }

  &.is-selected {
    color: #409eff;
    font-weight: 600;
  }
}

.ed-select-tipo__option-check {
  margin-right: 8px;
  margin-top: 2px;
  font-size: 14px;
}

.ed-select-tipo__option-content {
  flex: 1;
}

.ed-select-tipo__option-obs {
  color: #8492a6;
  font-size: 13px;
  font-weight: normal;
}

.ed-select-tipo__empty {
  padding: 10px;
  text-align: center;
  color: #999;
  font-size: 14px;
}
</style>



// WEBPACK FOOTER //
// EdSelectTipo.vue?456b28f5